import { useCallback, useEffect, useState } from "react";
import { formatPhone, pageStatus, type Page } from "@pagelink/shared";
import { PhotoEdit } from "./PhotoEdit.tsx";
import { refreshPhotos } from "./photos.ts";
import { api, ApiError, setBearer, type Actor } from "./api.ts";
import { usePath } from "./router.ts";
import { SignIn, Enrol } from "./Auth.tsx";
import { PageList } from "./PageList.tsx";
import { Compose } from "./Compose.tsx";
import { Detail } from "./Detail.tsx";
import { History } from "./History.tsx";
import { OnCall } from "./OnCall.tsx";
import { Teams } from "./Teams.tsx";
import { Stars } from "./Stars.tsx";
import { Billing } from "./Billing.tsx";
import { Admin } from "./Admin.tsx";
import { Avatar, Banner, Btn, Icon, clock } from "./ui.tsx";
import { enablePush, pushState, type PushState } from "./push.ts";
import { useStars } from "./stars.ts";
import { inTeams, teamsToken, teamsReady } from "./teams.ts";

/** How often the lists refresh. Polling, not sockets: one fewer thing to
 *  operate, and a few seconds of staleness is invisible next to the push. */
const POLL_MS = 4000;

type Tab = "pages" | "oncall" | "teams" | "history" | "stars" | "billing";
const TABS: [Tab, string, string, string][] = [
  ["pages", "Pages", "pager", "/"],
  ["oncall", "On-call", "calendar", "/on-call"],
  ["teams", "Groups", "users", "/teams"],
  ["history", "History", "archive", "/history"],
  ["stars", "Stars", "star", "/stars"],
  ["billing", "Billing", "receipt", "/billing"],
];
const tabOf = (path: string): Tab =>
  path === "/on-call" ? "oncall" : path === "/teams" ? "teams" : path === "/history" ? "history" : path === "/stars" ? "stars" : path === "/billing" ? "billing" : "pages";

export function App() {
  const [path, navigate] = usePath();
  const [actor, setActor] = useState<Actor | null>(null);
  const [vapid, setVapid] = useState("");
  const [booted, setBooted] = useState(false);
  const [incoming, setIncoming] = useState<Page[]>([]);
  const [outgoing, setOutgoing] = useState<Page[]>([]);
  const [dir, setDir] = useState<"in" | "out">("in");
  const [push, setPush] = useState<PushState>("prompt");
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [teams, setTeams] = useState(false);
  const [patientData, setPatientData] = useState(false);
  useEffect(() => { api.authConfig().then((c) => setPatientData(!!c.patientData)).catch(() => {}); }, []);
  const [teamsError, setTeamsError] = useState<string | null>(null);

  const loadMe = useCallback(async () => {
    try {
      const r = await api.me();
      setActor(r.actor);
      setVapid(r.vapidPublicKey);
      setPush(pushState());
    } catch {
      setActor(null);
    } finally {
      setBooted(true);
    }
  }, []);

  // Boot. Inside Teams: single sign-on first, then the normal load. Outside:
  // the normal load, which finds the passkey session cookie or shows sign-in.
  useEffect(() => {
    void (async () => {
      const framed = await inTeams();
      setTeams(framed);
      if (framed) {
        try {
          const token = await teamsToken();
          const r = await api.teamsVerify(token);
          setBearer(r.session);
        } catch (e) {
          setBearer(null);
          setTeamsError(e instanceof ApiError ? e.message : "Teams could not sign you in. Open page-com again from the Teams app list.");
        } finally {
          teamsReady();
        }
      }
      await loadMe();
    })();
  }, [loadMe]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);

  const refresh = useCallback(async () => {
    if (!actor) return;
    try {
      const r = await api.pages();
      setIncoming(r.incoming);
      setOutgoing(r.outgoing);
      setError(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setActor(null);
      else setError("Cannot reach the server.");
    }
  }, [actor]);

  useEffect(() => {
    if (!actor) return;
    void refresh();
    const t = setInterval(() => void refresh(), POLL_MS);
    const onMsg = () => void refresh();
    navigator.serviceWorker?.addEventListener("message", onMsg);
    return () => {
      clearInterval(t);
      navigator.serviceWorker?.removeEventListener("message", onMsg);
    };
  }, [actor, refresh]);

  if (!booted) return <main className="auth" />;

  if (path === "/enrol") {
    const token = new URLSearchParams(window.location.search).get("token") ?? "";
    return <Enrol token={token} onDone={() => { void loadMe().then(() => navigate("/")); }} />;
  }
  if (!actor) return <SignIn onDone={() => void loadMe()} teams={teams} teamsError={teamsError} patientData={patientData} />;

  return <Shell actor={actor} vapid={vapid} push={teams ? "unsupported" : push} setPush={setPush} error={error} teams={teams} patientData={patientData}
    incoming={incoming} outgoing={outgoing} dir={dir} setDir={setDir}
    path={path} navigate={navigate} now={now} profile={profile} setProfile={setProfile}
    onSignOut={() => api.logout().then(() => { setBearer(null); setActor(null); })} refresh={refresh} onActor={setActor}
    replace={(p) => {
      setIncoming((xs) => xs.map((x) => (x.id === p.id ? p : x)));
      setOutgoing((xs) => xs.map((x) => (x.id === p.id ? p : x)));
    }} />;
}

function Shell({ actor, vapid, push, setPush, error, teams, patientData, incoming, outgoing, dir, setDir, path, navigate, now, profile, setProfile, onSignOut, refresh, replace, onActor }: {
  actor: Actor; vapid: string; push: PushState; setPush: (s: PushState) => void; error: string | null; teams: boolean; patientData: boolean;
  incoming: Page[]; outgoing: Page[]; dir: "in" | "out"; setDir: (d: "in" | "out") => void;
  path: string; navigate: (to: string) => void; now: number; profile: boolean; setProfile: (b: boolean) => void;
  onSignOut: () => void; refresh: () => Promise<void>; replace: (p: Page) => void; onActor: (a: Actor) => void;
}) {
  const [stars, toggleStar] = useStars(actor.id);
  // Who has a profile photo; refreshed now and then so new pictures appear.
  useEffect(() => { void refreshPhotos(); const t = setInterval(refreshPhotos, 120_000); return () => clearInterval(t); }, [actor.id]);
  const tab = tabOf(path);
  const detailId = path.startsWith("/pages/") ? path.slice("/pages/".length) : null;
  // A page outside the inbox (archived, or older than the newest 200) is fetched on its own.
  const listed = detailId ? [...incoming, ...outgoing].find((p) => p.id === detailId) ?? null : null;
  const [extra, setExtra] = useState<Page | null>(null);
  const [missing, setMissing] = useState<string | null>(null);
  useEffect(() => {
    if (!detailId || listed) return;
    let live = true;
    api.page(detailId).then((r) => live && setExtra(r.page))
      .catch((e) => live && setMissing(e instanceof ApiError ? e.message : "Could not open that page."));
    return () => { live = false; };
  }, [detailId, !!listed]);
  const current = listed ?? (extra?.id === detailId ? extra : null);
  const composeQuery = path.startsWith("/new") ? new URLSearchParams(window.location.search) : null;
  const composeService = composeQuery?.get("service") ?? undefined;
  const composeSub = composeQuery?.get("sub") ?? null;
  const composeTeam = composeQuery?.get("team") ?? null;
  const composeTo = composeQuery?.get("to") ?? null;
  // Open incoming pages, plus any page with a message the viewer has not read.
  const openIn = new Set([
    ...incoming.filter((p) => !pageStatus(p).resolved).map((p) => p.id),
    ...[...incoming, ...outgoing].filter((p) => p.unread > 0).map((p) => p.id),
  ]).size;
  const badges: Partial<Record<Tab, number>> = { pages: openIn, stars: stars.size };

  const body = profile ? (
    <Profile actor={actor} push={push} teams={teams} onEnablePush={() => enablePush(vapid).then(setPush)} onClose={() => setProfile(false)} onSignOut={onSignOut} onActor={onActor}
      onAdmin={() => { setProfile(false); navigate("/admin"); }} />
  ) : path === "/admin" && actor.isAdmin ? (
    <Admin me={actor} onClose={() => navigate("/")} />
  ) : detailId ? (
    current
      ? <Detail page={current} siblings={current.broadcastId ? outgoing.filter((p) => p.broadcastId === current.broadcastId) : []} meId={actor.id} onBack={() => navigate("/")} onChange={(p) => { replace(p); if (extra?.id === p.id) setExtra(p); void refresh(); }} onOpen={(id) => navigate(`/pages/${id}`)} starred={stars.has(current.id)} onStar={() => toggleStar(current.id)} />
      : <p className="hint" style={{ padding: 20, textAlign: "center" }}>{missing ?? "Loading that page…"}</p>
  ) : path.startsWith("/new") ? (
    <Compose me={actor} initialService={composeService} initialSub={composeSub} initialTeam={composeTeam} initialTo={composeTo} patientData={patientData}
      onSent={(id) => { void refresh(); navigate(`/pages/${id}`); }}
      onCancel={() => navigate("/")} onOpenExisting={(id) => navigate(`/pages/${id}`)} />
  ) : tab === "oncall" ? (
    <OnCall onPage={(svc, sub) => navigate(`/new?service=${encodeURIComponent(svc)}${sub ? `&sub=${encodeURIComponent(sub)}` : ""}`)}
      onPagePerson={(id) => navigate(`/new?to=${encodeURIComponent(id)}`)} />
  ) : tab === "teams" ? (
    <Teams me={actor} onPage={(id) => navigate(`/new?team=${encodeURIComponent(id)}`)} />
  ) : tab === "history" ? (
    <History incoming={incoming} outgoing={outgoing} meId={actor.id} onOpen={(id) => navigate(`/pages/${id}`)} stars={stars} onStar={toggleStar} />
  ) : tab === "billing" ? (
    <Billing />
  ) : tab === "stars" ? (
    <Stars incoming={incoming} outgoing={outgoing} meId={actor.id} stars={stars} onStar={toggleStar} onOpen={(id) => navigate(`/pages/${id}`)} />
  ) : (
    <PageList incoming={incoming} outgoing={outgoing} meId={actor.id} onChanged={refresh} tab={dir} setTab={setDir}
      onOpen={(id) => navigate(`/pages/${id}`)} onCompose={() => navigate("/new")} stars={stars} onStar={toggleStar} />
  );

  const composing = path.startsWith("/new");
  // An open page is a conversation: it brings its own header and footer.
  const chatting = !!detailId && !profile;

  return (
    <div className="phone">
      {!chatting && <header className="phone-head">
        <span className="wordmark">page<span>-com</span></span>
        <span className="head-right">
          <span className="head-clock">{clock(now)}</span>
          <button className="avatar-btn" onClick={() => setProfile(!profile)} aria-label="Your profile" title="Your profile">
            <Avatar id={actor.id} name={actor.name} size={22} fontSize={11} />
          </button>
        </span>
      </header>}
      <div className={`phone-body${chatting ? " chatting" : ""}`}>
        {patientData && <div className="demo-strip" role="note">DEMO · made-up patients only · never enter real patient details</div>}
        {error && <Banner kind="err">{error}</Banner>}
        {!composing && !chatting && !profile && push !== "granted" && push !== "unsupported" && push !== "denied" && (
          <Banner kind="warn">
            Notifications are off. Pages will not reach you when the app is closed.
            <div style={{ marginTop: 6 }}><Btn sm onClick={() => enablePush(vapid).then(setPush)}><Icon name="bell" size={13} sw={2.2} />Turn on</Btn></div>
          </Banner>
        )}
        {!composing && !chatting && !profile && push === "denied" && (
          <Banner kind="err">Notifications are blocked in your phone's settings for this app.</Banner>
        )}
        {body}
      </div>
      {!composing && !chatting && (
        <nav className="tabbar" aria-label="Sections">
          {TABS.map(([k, label, icon, to]) => (
            <button key={k} aria-label={label} aria-current={!profile && tab === k ? "page" : undefined}
              onClick={() => { setProfile(false); navigate(to); }}>
              <span className="tab-ic">
                <Icon name={icon} size={19} sw={!profile && tab === k ? 2.1 : 1.75} />
                {(badges[k] ?? 0) > 0 && <span className="tab-badge">{badges[k]}</span>}
              </span>
              {label}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}

function Profile({ actor, push, teams, onEnablePush, onClose, onSignOut, onActor, onAdmin }: {
  actor: Actor; push: PushState; teams: boolean; onEnablePush: () => void; onClose: () => void; onSignOut: () => void;
  onActor: (a: Actor) => void; onAdmin: () => void;
}) {
  return (
    <>
      <button className="back" onClick={onClose}><Icon name="back" size={15} sw={2.2} />Back</button>
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0 14px" }}>
        <PhotoEdit id={actor.id} name={actor.name} onSave={(d) => api.setMyPhoto(d)} onRemove={() => api.removeMyPhoto()} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: "var(--disp)", fontWeight: 800, fontSize: 18, color: "var(--ink)" }}>{actor.name}</div>
          <div style={{ fontSize: 12.5, color: "var(--dim)" }}>{actor.title} · {actor.service}{actor.isAdmin ? " · Admin" : ""}</div>
        </div>
      </div>
      <div className="sec">Notifications</div>
      <div className="sheet-row">
        <span className="k">Lock-screen alerts</span>
        <span className="v">{teams ? "Through Teams" : push === "granted" ? "On" : push === "denied" ? "Blocked in settings" : push === "unsupported" ? "Not available in this browser" : "Off"}</span>
      </div>
      {!teams && push === "prompt" && <div className="stack"><Btn full onClick={onEnablePush}><Icon name="bell" size={14} sw={2.2} />Turn on notifications</Btn></div>}
      {!teams && push === "unsupported" && <div className="note" style={{ marginTop: 6 }}>On iPhone, add page-com to the home screen first and open it from there.</div>}
      {teams && <div className="note" style={{ marginTop: 6 }}>Inside Teams, pages reach you as Teams alerts. Keep Teams notifications on for this phone.</div>}
      {actor.isAdmin && (
        <div className="stack" style={{ marginTop: 6 }}>
          <Btn full soft onClick={onAdmin}><Icon name="users" size={14} sw={2.2} />Admin: people, rota, services, fee codes</Btn>
        </div>
      )}
      <Contact actor={actor} onActor={onActor} />
      <div className="sec" style={{ marginTop: 18 }}>Site</div>
      <div className="sheet-row"><span className="k">Hospital</span><span className="v">{actor.siteId.toUpperCase()}</span></div>
      <div style={{ marginTop: 24 }}>
        <Btn full soft tone="dim" onClick={onSignOut}><Icon name="exit" size={14} sw={2} />Sign out</Btn>
      </div>
    </>
  );
}

/* Email and the two phone numbers. The primary phone is prefilled as the
 * callback on every page; both appear as one-tap choices in the composer. */
function Contact({ actor, onActor }: { actor: Actor; onActor: (a: Actor) => void }) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(actor.email ?? "");
  const [phone, setPhone] = useState(actor.callback ?? "");
  const [phoneAlt, setPhoneAlt] = useState(actor.phoneAlt ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.saveProfile({ email, phone, phoneAlt });
      onActor(r.actor);
      setEditing(false);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const digits = (v: string) => v.replace(/[^0-9+]/g, "");
  return (
    <>
      <div className="sec" style={{ marginTop: 18, display: "flex", alignItems: "center" }}>
        <span style={{ flex: 1 }}>Contact</span>
        {!editing && <button className="linkish" onClick={() => setEditing(true)}>Edit</button>}
      </div>
      {!editing ? (
        <>
          <div className="sheet-row"><span className="k">Email</span><span className="v">{actor.email ?? "not set"}</span></div>
          <div className="sheet-row"><span className="k">Primary phone · your callback</span><span className="v mono">{actor.callback ? formatPhone(actor.callback) : "not set"}</span></div>
          <div className="sheet-row"><span className="k">Alternative phone</span><span className="v mono">{actor.phoneAlt ? formatPhone(actor.phoneAlt) : "not set"}</span></div>
        </>
      ) : (
        <div className="form" style={{ padding: 0 }}>
          {err && <Banner kind="err">{err}</Banner>}
          <div className="fsec">
            <label htmlFor="pf-email">Email</label>
            <input id="pf-email" type="email" autoComplete="email" value={email} maxLength={120} onChange={(e) => setEmail(e.target.value)} placeholder="name@vch.ca" />
          </div>
          <div className="fsec">
            <label htmlFor="pf-phone">Primary phone</label>
            <input id="pf-phone" className="mono" type="tel" inputMode="tel" value={phone} maxLength={16} onChange={(e) => setPhone(digits(e.target.value))} placeholder="Extension or mobile" />
          </div>
          <div className="fsec">
            <label htmlFor="pf-alt">Alternative phone <span style={{ fontWeight: 400, color: "var(--faint)" }}>(optional)</span></label>
            <input id="pf-alt" className="mono" type="tel" inputMode="tel" value={phoneAlt} maxLength={16} onChange={(e) => setPhoneAlt(digits(e.target.value))} placeholder="Another number to call you on" />
          </div>
          <div className="row">
            <Btn full soft tone="dim" disabled={busy} onClick={() => { setEditing(false); setErr(null); setEmail(actor.email ?? ""); setPhone(actor.callback ?? ""); setPhoneAlt(actor.phoneAlt ?? ""); }}>Cancel</Btn>
            <Btn full disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</Btn>
          </div>
        </div>
      )}
    </>
  );
}
